import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation, useOutlet } from 'react-router';

import { FoodSearchProvider } from '@/client-search/useFoodSearchIndex';
import { FeatureGate } from '@/components/FeatureGate';
import { FoodDetailsPopupSingletonContainer } from '@/components/FoodDetailsPopupSingletonContainer';
import { LazyCommandPalette } from '@/components/kbar/LazyCommandPalette';
import { LocalStoragePopup } from '@/components/LocalStoragePopup';
import { lazyNamed, RouteFallback } from '@/components/lazyRoute';
import { NewBuildNotice } from '@/components/NewBuildNotice';
import { PreviewBuildAgeGate } from '@/components/PreviewBuildAgeGate';
import { Spinner, TooltipProvider } from '@/components/ui';
import { AppContextProvider, useAuth } from '@/context/appContext';
import { FeatureContextProvider } from '@/context/FeatureContext';
import { FullRegionalApis } from '@/context/FullRegionalApis';
import { SessionQueryProvider } from '@/context/SessionQueryProvider';
import { isLocalUiScenario } from '@/dev/localUiScenario';
import { Page } from '@/layout/Page';
import { Login } from '@/pages/Login';
import NotFound from '@/pages/NotFound';
import { QueuePage } from '@/pages/Queue';
import { DialogGlobalComponent } from '@/services/DialogService';
import { MealQueueNotificationService } from '@/services/MealQueueNotificationService';
import { useActiveQueuePeriodRefetch } from '@/services/MealQueueService';
import { SnackBarGlobalComponent } from '@/services/SnackBarService';
import { useSocketIOConnect, useSocketIOPresenceIdent, useSocketIOPresenceSubscribe } from '@/socketio/SocketIOService';
import { useTelemetrySendMetrics_DISABLED, useTelemetryTimeOnPage_DISABLED } from '@/utils/telemetry';

// Route pages load on demand so the entry chunk only carries the shell, login and the queue landing page.
const ChefAdminPage = lazyNamed(() => import('@/pages/ChefAdminPage'), 'ChefAdminPage');
const CreateFoodPage = lazyNamed(() => import('@/pages/CreateFoodPage'), 'CreateFoodPage');
const CustomMealItems = lazyNamed(() => import('@/pages/CustomMealItems'), 'CustomMealItems');
const EngPage = lazyNamed(() => import('@/pages/Eng'), 'EngPage');
const FeedbackPage = lazyNamed(() => import('@/pages/Feedback'), 'FeedbackPage');
const FoodSearchPage = lazyNamed(() => import('@/pages/FoodSearch'), 'FoodSearchPage');
const FoodSearchTest = lazyNamed(() => import('@/pages/FoodSearchTest'), 'FoodSearchTest');
const InitialQAPage = lazyNamed(() => import('@/pages/InitialQA'), 'InitialQAPage');
const MealEntryTest = lazyNamed(() => import('@/pages/MealEntryTest'), 'MealEntryTest');
const QAPage = lazyNamed(() => import('@/pages/QA'), 'QAPage');
const MealOrQueueItemPage = lazyNamed(() => import('@/pages/QueueItem/QueueItemPage'), 'MealOrQueueItemPage');
const RecipeImportPage = lazyNamed(() => import('@/pages/RecipeImportPage'), 'RecipeImportPage');
const RecipeScanPage = lazyNamed(() => import('@/pages/RecipeScanPage'), 'RecipeScanPage');
const RecipeScanReviewPage = lazyNamed(() => import('@/pages/RecipeScanReviewPage'), 'RecipeScanReviewPage');
const RecipeEditorPage = lazyNamed(() => import('@/pages/RecipesPage'), 'RecipeEditorPage');
const RecipeListPage = lazyNamed(() => import('@/pages/RecipesPage'), 'RecipeListPage');
const RecipeUsagePage = lazyNamed(() => import('@/pages/RecipeUsagePage'), 'RecipeUsagePage');
const ReplayLogsPage = lazyNamed(() => import('@/pages/ReplayLogs'), 'ReplayLogsPage');
const ReviewPage = lazyNamed(() => import('@/pages/Review'), 'ReviewPage');
const SupportMealTracePage = lazyNamed(
  () => import('@/pages/SupportMealTrace/SupportMealTracePage'),
  'SupportMealTracePage',
);
const MyFoodTablePage = lazyNamed(() => import('@/components/FoodDatabaseTable'), 'MyFoodTablePage');
const BrandManagementPage = lazyNamed(() => import('@/pages/BrandManagementPage'), 'BrandManagementPage');
const FindingsInboxPage = lazyNamed(() => import('@/pages/findings/FindingsInboxPage'), 'FindingsInboxPage');
const MealFeedbackReviewPage = lazyNamed(
  () => import('@/pages/findings/MealFeedbackReviewPage'),
  'MealFeedbackReviewPage',
);
const MiaReviewItemPage = lazyNamed(() => import('@/pages/miaReview/MiaReviewItemPage'), 'MiaReviewItemPage');
const MiaReviewQueuePage = lazyNamed(() => import('@/pages/miaReview/MiaReviewQueuePage'), 'MiaReviewQueuePage');
const FoodEnginePage = lazyNamed(() => import('@/pages/FoodEnginePage'), 'FoodEnginePage');
// Internal test page with an 86 kB data file: left out of production builds, kept on staging and locally.
const FoodOptionsTest =
  import.meta.env.VITE_IA_ENV === 'production'
    ? null
    : lazyNamed(() => import('@/pages/FoodOptionsTest'), 'FoodOptionsTest');
const FoodTablePage = lazyNamed(() => import('@/pages/FoodTablePage'), 'FoodTablePage');
const GroundingRequestDetailPage = lazyNamed(
  () => import('@/pages/GroundingRequestsPage'),
  'GroundingRequestDetailPage',
);
const GroundingRequestListPage = lazyNamed(() => import('@/pages/GroundingRequestsPage'), 'GroundingRequestListPage');
const MealGalleryPage = lazyNamed(() => import('@/pages/MealGalleryPage'), 'MealGalleryPage');
const PatientReportItemPage = lazyNamed(() => import('@/pages/PatientReportItemPage'), 'PatientReportItemPage');
const PatientReportListPage = lazyNamed(() => import('@/pages/PatientReportListPage'), 'PatientReportListPage');
const RestaurantMenuDetailPage = lazyNamed(() => import('@/pages/RestaurantMenusPage'), 'RestaurantMenuDetailPage');
const RestaurantMenuListPage = lazyNamed(() => import('@/pages/RestaurantMenusPage'), 'RestaurantMenuListPage');
const ScrapeFoodTablePage = lazyNamed(() => import('@/pages/ScrapeFoodTablePage'), 'ScrapeFoodTablePage');
const TriageFoodTablePage = lazyNamed(() => import('@/pages/TriageFoodTablePage'), 'TriageFoodTablePage');
const UpdateFoodPage = lazyNamed(() => import('@/pages/UpdateFoodPage'), 'UpdateFoodPage');

// Dev-only tooling is behind `import.meta.env.DEV`, so production builds never contain it.
const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(async () => ({ default: (await import('@tanstack/react-query-devtools')).ReactQueryDevtools }))
  : null;
const UiScenarioApp = import.meta.env.DEV
  ? lazy(async () => ({ default: (await import('@/dev/UiScenarioApp')).UiScenarioApp }))
  : null;

const App = () => {
  return (
    <AppContextProvider>
      <SessionQueryProvider>
        <TooltipProvider delayDuration={100}>
          <FeatureContextProvider>
            <FoodSearchProvider>
              <LocalStoragePopup />
              <MealQueueNotificationService />
              <Editor />
              <LazyCommandPalette />
              <SnackBarGlobalComponent />
              <DialogGlobalComponent />
              <PreviewBuildAgeGate />
              <NewBuildNotice />
              <FoodDetailsPopupSingletonContainer />
            </FoodSearchProvider>
          </FeatureContextProvider>
        </TooltipProvider>
        {ReactQueryDevtools && (
          <Suspense fallback={null}>
            <ReactQueryDevtools initialIsOpen={false} />
          </Suspense>
        )}
      </SessionQueryProvider>
    </AppContextProvider>
  );
};

const useAppSingletonServices = () => {
  useTelemetryTimeOnPage_DISABLED();
  useTelemetrySendMetrics_DISABLED();
  useSocketIOConnect();
  useSocketIOPresenceIdent();
  useSocketIOPresenceSubscribe();
  useActiveQueuePeriodRefetch();
};

const Editor = () => {
  const auth = useAuth();
  useAppSingletonServices();

  if (UiScenarioApp && isLocalUiScenario()) {
    return (
      <Suspense fallback={null}>
        <UiScenarioApp />
      </Suspense>
    );
  }

  if (!auth.initialRes.isDone) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="size-6 text-brand" label="Loading application" />
      </div>
    );
  }

  return (
    <Routes>
      <Route element={<AuthedRoutes />}>
        <Route element={<Page />}>
          <Route path="/" element={<QueuePage />} />
          <Route
            element={
              <Suspense fallback={<RouteFallback />}>
                <FullRegionalApis />
              </Suspense>
            }
          >
            <Route path="review" element={<ReviewPage />} />
            <Route path="custom-meal-items" element={<CustomMealItems />} />
            {FoodOptionsTest && <Route path="food-options-test" element={<FoodOptionsTest />} />}
            <Route path="qa" element={<QAPage />} />
            <Route path="foods/new" element={<CreateFoodPage />} />
            <Route path="foods/*" element={<UpdateFoodPage />} />
            <Route path="food" element={<UpdateFoodPage />} /> {/* legacy */}
            <Route path="foods" element={<FoodSearchPage />} />
            <Route path="food-search" element={<FoodSearchPage />} /> {/* legacy */}
            <Route path="food/engine" element={<FoodEnginePage />} />
            <Route path="eng" element={<EngPage />} />
            <Route path="food-search-test" element={<FoodSearchTest />} />
            <Route path="meal-entry-test" element={<MealEntryTest />} />
            <Route path="dev/replaylogs" element={<ReplayLogsPage />} />
            <Route path="qa/initial-qa" element={<InitialQAPage />} />
            <Route path="brands" element={<BrandManagementPage />} />
            <Route path="restaurant-menus" element={<RestaurantMenuListPage />} />
            <Route path="restaurant-menus/:menuId" element={<RestaurantMenuDetailPage />} />
            <Route path="grounding/requests" element={<GroundingRequestListPage />} />
            <Route path="grounding/requests/:requestId" element={<GroundingRequestDetailPage />} />
            <Route path="recipes" element={<RecipeListPage />} />
            <Route path="recipes/needs-attention" element={<RecipeListPage needsAttention={true} />} />
            <Route path="recipes/import" element={<RecipeImportPage />} />
            <Route path="recipes/create/:source" element={<RecipeScanPage />} />
            {/* Older links kept working; photo is the common case. */}
            <Route path="recipes/scan" element={<Navigate to="/recipes/create/photo" replace />} />
            <Route path="recipes/usage" element={<RecipeUsagePage />} />
            <Route path="chefs" element={<ChefAdminPage />} />
          </Route>
        </Route>
        <Route element={<Page fullWidth={true} />}>
          <Route
            element={
              <Suspense fallback={<RouteFallback />}>
                <FullRegionalApis />
              </Suspense>
            }
          >
            {/* Wide data tables use the full screen width so their column labels fit. */}
            <Route path="feedback" element={<FeedbackPage />} />
            <Route path="foods/table" element={<FoodTablePage />} />
            <Route path="foods/table/custom" element={<TriageFoodTablePage />} />
            <Route path="foods/table/triaged" element={<FoodTablePage />} />
            <Route path="foods/table/triaged-recipes" element={<FoodTablePage />} />
            <Route path="foods/table/triaged-cpg" element={<ScrapeFoodTablePage />} />
            <Route path="foods/table/draft" element={<FoodTablePage />} />
            <Route path="foods/table/review" element={<FoodTablePage />} />
            <Route path="foods/table/my-foods" element={<MyFoodTablePage />} />
            <Route path="patient-reports" element={<PatientReportListPage />} />
            {/* Recipe editing and matching use the full screen width. */}
            <Route path="recipes/new" element={<RecipeEditorPage />} />
            <Route path="recipes/:recipeId" element={<RecipeEditorPage />} />
            <Route path="recipes/:recipeId/review" element={<RecipeScanReviewPage />} />
            <Route path="patient-reports/:itemId" element={<PatientReportItemPage />} />
            <Route path="queue-item/:itemId" element={<MealOrQueueItemPage />} />
            <Route path="meal/:mealId" element={<MealOrQueueItemPage />} />
            <Route path="qa/session/:sessionId/queue/:itemId" element={<MealOrQueueItemPage />} />
            <Route path="meals/browse" element={<MealGalleryPage />} />
            <Route
              path="findings"
              element={
                <FeatureGate feature="findings_inbox">
                  <FindingsInboxPage />
                </FeatureGate>
              }
            />
            <Route
              path="findings/:findingId"
              element={
                <FeatureGate feature="findings_inbox">
                  <MealFeedbackReviewPage />
                </FeatureGate>
              }
            />
            <Route
              path="mia-review"
              element={
                <FeatureGate feature="mia_review">
                  <MiaReviewQueuePage />
                </FeatureGate>
              }
            />
            <Route
              path="mia-review/:itemId"
              element={
                <FeatureGate feature="mia_review">
                  <MiaReviewItemPage />
                </FeatureGate>
              }
            />
            <Route path="support/meal-trace" element={<SupportMealTracePage />} />
            <Route path="support/meal-trace/:mpqId" element={<SupportMealTracePage />} />
          </Route>
        </Route>
      </Route>

      <Route path="login" element={<Login />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const AuthedRoutes = () => {
  const { authInfo } = useAuth();
  const outlet = useOutlet();
  const loc = useLocation();

  const nextUrl = loc.pathname + loc.search + loc.hash;

  const loginPath = '/login' + (nextUrl.length <= 1 ? '' : `?next=${encodeURIComponent(nextUrl)}`);
  if (!authInfo) {
    return <Navigate to={loginPath} />;
  }

  return outlet;
};

export { App };
